import clsx from "clsx";
import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from "react";
import { Field, controlClasses, describedBy } from "./Field";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  containerClassName?: string;
  /** Ctrl/Cmd+Enter submits from inside the textarea (spec §5). */
  onSubmitShortcut?: () => void;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    containerClassName,
    className,
    id,
    required,
    rows = 6,
    onSubmitShortcut,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const generated = useId();
  const textareaId = id ?? generated;
  return (
    <Field
      id={textareaId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(textareaId, hint, error)}
        className={clsx(controlClasses, "resize-y", error && "border-red-600", className)}
        onKeyDown={(event) => {
          if (onSubmitShortcut && (event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.preventDefault();
            onSubmitShortcut();
          }
          onKeyDown?.(event);
        }}
        {...rest}
      />
    </Field>
  );
});
